/**
 * Le détail d'une ligne du journal, lisible : « champ : valeur », ou
 * « champ : avant → après » pour une modification. L'API envoie les
 * métadonnées brutes (`metadonnees`) et, à côté, un texte `cle=valeur · …`
 * (`details`) : on préfère les premières, on lit le second à défaut — une API
 * plus ancienne, ou une fixture.
 *
 * Fonction pure : tout ce qui dépend de la langue arrive par `ContexteJournal`.
 */

export interface LigneDetail {
  /** Le nom du champ, déjà traduit ; vide pour un texte libre. */
  champ: string;
  valeur?: string;
  avant?: string;
  apres?: string;
}

export interface ContexteJournal {
  /** Libellé d'un champ de métadonnées (`jobId` → « Travail »). */
  champ: (cle: string) => string;
  /** Libellé d'un module (`newsletter` → « Newsletter »). */
  module: (id: string) => string;
  oui: string;
  non: string;
  /** Ce qu'on écrit pour « rien » (`null`, absent). */
  vide: string;
  /** Date et heure lisibles d'une chaîne ISO. */
  date: (iso: string) => string;
  /** Un libellé propre à certaines valeurs (le rôle `venio_direction` → « Direction »), sinon `null`. */
  valeur?: (cle: string, valeur: string) => string | null;
}

export interface EntreeADetailler {
  details: string | null;
  metadonnees?: Record<string, unknown> | undefined;
}

/** Les champs qui portent des identifiants de modules. */
const CHAMPS_MODULE = new Set(['module', 'modules', 'modulesActifs']);

/** Le rôle de l'acteur accompagne chaque ligne de console : on le lit en dernier. */
const CHAMPS_EN_FIN = ['roleConsole'];

const ISO = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/;

function estObjet(valeur: unknown): valeur is Record<string, unknown> {
  return typeof valeur === 'object' && valeur !== null && !Array.isArray(valeur);
}

function formater(valeur: unknown, cle: string, ctx: ContexteJournal, profondeur = 0): string {
  if (valeur === null || valeur === undefined) return ctx.vide;
  if (typeof valeur === 'boolean') return valeur ? ctx.oui : ctx.non;
  if (typeof valeur === 'number') return String(valeur);
  if (typeof valeur === 'string') {
    if (ISO.test(valeur)) return ctx.date(valeur);
    if (CHAMPS_MODULE.has(cle)) return ctx.module(valeur);
    return ctx.valeur?.(cle, valeur) ?? valeur;
  }
  if (Array.isArray(valeur)) {
    return valeur.map((element) => formater(element, cle, ctx, profondeur + 1)).join(', ');
  }
  if (estObjet(valeur)) {
    // Un objet n'est jamais rendu « [object Object] » : ses champs, à plat.
    if (profondeur >= 2) return JSON.stringify(valeur);
    return Object.entries(valeur)
      .map(
        ([sousCle, sousValeur]) =>
          `${sousCle}: ${formater(sousValeur, sousCle, ctx, profondeur + 1)}`,
      )
      .join(', ');
  }
  return String(valeur);
}

function egales(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** « avant / après » : champ par champ si ce sont des objets, d'un bloc sinon. */
function comparer(avant: unknown, apres: unknown, ctx: ContexteJournal): LigneDetail[] {
  if (estObjet(apres)) {
    const ancien = estObjet(avant) ? avant : {};
    // `apres` ne porte que ce qui a changé : ses clés disent quoi comparer.
    return Object.keys(apres)
      .filter((cle) => !egales(ancien[cle], apres[cle]))
      .map((cle) => ({
        champ: ctx.champ(cle),
        avant: formater(ancien[cle], cle, ctx),
        apres: formater(apres[cle], cle, ctx),
      }));
  }
  return [
    {
      champ: ctx.champ('valeur'),
      avant: formater(avant, 'valeur', ctx),
      apres: formater(apres, 'valeur', ctx),
    },
  ];
}

function depuisMetadonnees(
  metadonnees: Record<string, unknown>,
  ctx: ContexteJournal,
): LigneDetail[] {
  const lignes: LigneDetail[] = [];
  const cles = Object.keys(metadonnees).filter((cle) => metadonnees[cle] !== undefined);
  const ordre = [
    ...cles.filter((cle) => !CHAMPS_EN_FIN.includes(cle)),
    ...cles.filter((cle) => CHAMPS_EN_FIN.includes(cle)),
  ];
  for (const cle of ordre) {
    if (cle === 'avant' || cle === 'apres') continue;
    lignes.push({ champ: ctx.champ(cle), valeur: formater(metadonnees[cle], cle, ctx) });
  }
  if ('avant' in metadonnees || 'apres' in metadonnees) {
    // Les modifications passent avant le contexte (motif, rôle…).
    lignes.unshift(...comparer(metadonnees['avant'], metadonnees['apres'], ctx));
  }
  return lignes;
}

/** Le texte `cle=valeur · cle=valeur` de l'API, rendu en lignes. */
function depuisTexte(texte: string, ctx: ContexteJournal): LigneDetail[] {
  const morceaux = texte.split(' · ');
  const tousCleValeur = morceaux.every((morceau) => /^[A-Za-z][A-Za-z0-9_]*=/.test(morceau));
  if (!tousCleValeur) return [{ champ: '', valeur: texte }];
  return morceaux.map((morceau) => {
    const egal = morceau.indexOf('=');
    const cle = morceau.slice(0, egal);
    const brute = morceau.slice(egal + 1);
    let valeur: unknown = brute;
    try {
      // L'API écrit les non-chaînes en JSON (`true`, `3`, `["a"]`, `{"x":1}`).
      valeur = JSON.parse(brute) as unknown;
    } catch {
      /* une chaîne telle quelle */
    }
    return { champ: ctx.champ(cle), valeur: formater(valeur, cle, ctx) };
  });
}

export function lignesDeDetail(entree: EntreeADetailler, ctx: ContexteJournal): LigneDetail[] {
  if (entree.metadonnees && Object.keys(entree.metadonnees).length > 0) {
    return depuisMetadonnees(entree.metadonnees, ctx);
  }
  if (entree.details) return depuisTexte(entree.details, ctx);
  return [];
}
